import { LoadingButton } from '@mui/lab';
import {
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Typography,
} from '@mui/material';
import { useCallback, useMemo, useState } from 'react';

type BrandSegment = {
  id?: string | number | null;
  brandId: string | number;
  name?: string | null;
};

export interface CopyConfigModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (sourceBrandId: string, targetBrandIds: string[]) => Promise<void>;
  segments: BrandSegment[];
  loading: boolean;
}

export const CopyConfigModal = ({
  open,
  onClose,
  onSubmit,
  segments,
  loading,
}: CopyConfigModalProps) => {
  const [sourceBrandId, setSourceBrandId] = useState('');
  const [targetBrandIds, setTargetBrandIds] = useState<string[]>([]);

  const targetOptions = useMemo(
    () => segments.filter((s) => String(s.brandId) !== sourceBrandId),
    [segments, sourceBrandId],
  );

  const sourceOptions = useMemo(
    () => segments.filter((s) => !targetBrandIds.includes(String(s.brandId))),
    [segments, targetBrandIds],
  );

  const segmentNameMap = useMemo(
    () => new Map(segments.map((s) => [String(s.brandId), s.name || 'Untitled segment'])),
    [segments],
  );

  const canSubmit = Boolean(sourceBrandId && targetBrandIds.length > 0 && !loading);

  const handleClose = useCallback(() => {
    setSourceBrandId('');
    setTargetBrandIds([]);
    onClose();
  }, [onClose]);

  const handleSourceChange = useCallback((e: SelectChangeEvent) => {
    const value = e.target.value;
    setSourceBrandId(value);
    setTargetBrandIds((prev) => prev.filter((id) => id !== value));
  }, []);

  const handleTargetChange = useCallback((e: SelectChangeEvent<string[]>) => {
    const value = e.target.value;
    setTargetBrandIds(typeof value === 'string' ? value.split(',') : value);
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!canSubmit) return;
    await onSubmit(sourceBrandId, targetBrandIds);
    handleClose();
  }, [canSubmit, sourceBrandId, targetBrandIds, onSubmit, handleClose]);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Copy configuration</DialogTitle>
      <DialogContent>
        <Stack spacing={3} pt={1}>
          <Typography variant="body2" color="text.secondary">
            Copy the activation flow configuration (plans, pricing, features, and options) from one
            client segment to others. Each target segment&apos;s existing configuration will be
            overwritten.
          </Typography>

          <FormControl fullWidth size="small">
            <InputLabel id="copy-config-source-label">Source segment</InputLabel>
            <Select
              labelId="copy-config-source-label"
              value={sourceBrandId}
              label="Source segment"
              onChange={handleSourceChange}
            >
              {sourceOptions.map((segment) => (
                <MenuItem key={segment.brandId} value={String(segment.brandId)}>
                  {segment.name || 'Untitled segment'}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth size="small">
            <InputLabel id="copy-config-target-label">Target segments</InputLabel>
            <Select
              labelId="copy-config-target-label"
              multiple
              value={targetBrandIds}
              label="Target segments"
              onChange={handleTargetChange}
              renderValue={(selected) => (
                <Stack direction="row" flexWrap="wrap" gap={0.5}>
                  {selected.map((id) => (
                    <Chip key={id} label={segmentNameMap.get(id) ?? id} size="small" />
                  ))}
                </Stack>
              )}
            >
              {targetOptions.map((segment) => {
                const value = String(segment.brandId);
                return (
                  <MenuItem key={segment.brandId} value={value}>
                    <Checkbox checked={targetBrandIds.includes(value)} size="small" />
                    <ListItemText primary={segment.name || 'Untitled segment'} />
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <LoadingButton
          variant="contained"
          onClick={handleSubmit}
          loading={loading}
          disabled={!canSubmit}
        >
          Copy
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};
